import { Head, router, useForm, usePage } from "@inertiajs/react"
import { Lock, Mail, Palette, X } from "lucide-react"
import { useEffect, useRef, useState } from "react"

import { RebulkLogo } from "@/components/rebulk-logo"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { useOrgRoutes } from "@/hooks/use-org-routes"
import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem, SharedProps } from "@/types"

interface OrgProps {
  id: number
  name: string
  slug: string
  primary_color?: string
  secondary_color?: string
  theme?: "light" | "dark"
  weight_unit?: "lbs" | "tons" | "tonnes"
  logo_url?: string
  pdf_logo_url?: string
  email_subdomain?: string | null
}

interface Props {
  organization: OrgProps
  is_superadmin: boolean
}

const SUBDOMAIN_FORMAT = /^[a-z0-9-]{2,32}$/

export default function OrganizationSettings({
  organization,
  is_superadmin: isSuperadmin,
}: Props) {
  const routes = useOrgRoutes()
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "All Facilities", href: "/" },
    { title: "Organization", href: `/organizations/${organization.id}` },
  ]

  // Shared `organization` prop carries parent_org_id; a managed child org shows
  // a muted banner so admins know branding/units are scoped under their partner.
  const sharedOrg = usePage<SharedProps>().props.organization
  const isManaged = !!sharedOrg?.parent_org_id

  const logoRef = useRef<HTMLInputElement>(null)
  const pdfLogoRef = useRef<HTMLInputElement>(null)
  const form = useForm({
    name: organization.name,
    primary_color: organization.primary_color || "",
    secondary_color: organization.secondary_color || "",
    theme: organization.theme || "light",
    weight_unit: organization.weight_unit || "lbs",
    email_subdomain: organization.email_subdomain || "",
    logo: null as File | null,
    pdf_logo: null as File | null,
    logo_clear: false,
    pdf_logo_clear: false,
  })

  // Object URLs for the local file previews. URL.createObjectURL is
  // synchronous and cheap, but each call leaks the underlying blob if
  // not revoked — useEffect cleanup handles that on file change or
  // unmount. Without this, picking 5 logos in a row leaks 5 blob URLs.
  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  const [pdfLogoPreview, setPdfLogoPreview] = useState<string | null>(null)

  useEffect(() => {
    if (!form.data.logo) {
      setLogoPreview(null)
      return
    }
    const url = URL.createObjectURL(form.data.logo)
    setLogoPreview(url)
    return () => URL.revokeObjectURL(url)
  }, [form.data.logo])

  useEffect(() => {
    if (!form.data.pdf_logo) {
      setPdfLogoPreview(null)
      return
    }
    const url = URL.createObjectURL(form.data.pdf_logo)
    setPdfLogoPreview(url)
    return () => URL.revokeObjectURL(url)
  }, [form.data.pdf_logo])

  // Effective state per slot:
  //   - has a NEW pick (preview URL) → show that
  //   - cleared (logo_clear set) → show Rebulk default
  //   - has saved logo URL → show that
  //   - none of the above → show Rebulk default
  const logoToShow =
    logoPreview ??
    (form.data.logo_clear ? null : (organization.logo_url ?? null))
  const pdfLogoToShow =
    pdfLogoPreview ??
    (form.data.pdf_logo_clear ? null : (organization.pdf_logo_url ?? null))

  const clearLogo = () => {
    form.setData("logo", null)
    form.setData("logo_clear", true)
    if (logoRef.current) logoRef.current.value = ""
  }
  const clearPdfLogo = () => {
    form.setData("pdf_logo", null)
    form.setData("pdf_logo_clear", true)
    if (pdfLogoRef.current) pdfLogoRef.current.value = ""
  }

  function save(e: React.FormEvent) {
    e.preventDefault()
    if (!subdomainIsValid) return
    // POST + _method override is needed for multipart file upload support
    // (Rails can't parse multipart on PATCH from the browser fetch path).
    router.post(
      routes.orgSettingsPath(),
      {
        _method: "patch",
        organization: {
          name: form.data.name,
          primary_color: form.data.primary_color,
          secondary_color: form.data.secondary_color,
          theme: form.data.theme,
          weight_unit: form.data.weight_unit,
          email_subdomain: form.data.email_subdomain,
          ...(form.data.logo ? { logo: form.data.logo } : {}),
          ...(form.data.pdf_logo ? { pdf_logo: form.data.pdf_logo } : {}),
          ...(form.data.logo_clear ? { logo_clear: "true" } : {}),
          ...(form.data.pdf_logo_clear ? { pdf_logo_clear: "true" } : {}),
        },
      },
      { forceFormData: true },
    )
  }

  const trimmedSubdomain = form.data.email_subdomain.trim()
  const subdomainIsValid =
    trimmedSubdomain.length === 0 || SUBDOMAIN_FORMAT.test(trimmedSubdomain)

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Settings — ${organization.name}`} />

      <div className="flex flex-col p-6">
        <div className="mb-8 space-y-2 border-b pb-6">
          <h1 className="text-2xl font-semibold">Organization Settings</h1>
          <p className="text-muted-foreground text-sm">
            Branding, units, and inbound mail routing for {organization.name}.
          </p>
          {isManaged && (
            <p className="text-muted-foreground bg-muted/40 rounded-md border px-3 py-2 text-xs">
              Managed by your partner.
            </p>
          )}
        </div>

        <form onSubmit={save} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="h-5 w-5" /> Branding
              </CardTitle>
              <CardDescription>
                White-label your cloud portal with your organization's identity.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="max-w-md space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Organization Name</Label>
                  <Input
                    id="name"
                    value={form.data.name}
                    onChange={(e) => form.setData("name", e.target.value)}
                  />
                </div>
                {/* Platform logo — shown in the sidebar, page headers,
                    and the org switcher. When unset, the platform shows
                    the Rebulk 'R' icon as the default. */}
                <div className="space-y-2">
                  <Label htmlFor="logo">Platform Logo</Label>
                  <LogoPreview
                    src={logoToShow}
                    altWhenSet={`${organization.name} platform logo`}
                    rebulkDefaultKind="icon"
                    isLocalPreview={Boolean(logoPreview)}
                    onRemove={logoToShow || logoPreview ? clearLogo : undefined}
                  />
                  <Input
                    id="logo"
                    ref={logoRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    onChange={(e) => {
                      const file = e.target.files?.[0] ?? null
                      form.setData("logo", file)
                      // Picking a new file cancels a pending clear so
                      // the upload wins.
                      if (file) form.setData("logo_clear", false)
                    }}
                  />
                  <p className="text-muted-foreground text-xs">
                    Shown in the sidebar, page headers, and the org switcher.
                    Defaults to the Rebulk icon when empty. PNG, JPEG, WebP, or
                    GIF.
                  </p>
                </div>

                {/* PDF logo — separate slot for the header band on
                    branded Reports + Invoicing PDFs. When unset, the
                    full Rebulk wordmark is rendered as the default. */}
                <div className="space-y-2">
                  <Label htmlFor="pdf_logo">PDF Logo</Label>
                  <LogoPreview
                    src={pdfLogoToShow}
                    altWhenSet={`${organization.name} PDF logo`}
                    rebulkDefaultKind="wordmark"
                    isLocalPreview={Boolean(pdfLogoPreview)}
                    onRemove={
                      pdfLogoToShow || pdfLogoPreview ? clearPdfLogo : undefined
                    }
                  />
                  <Input
                    id="pdf_logo"
                    ref={pdfLogoRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    onChange={(e) => {
                      const file = e.target.files?.[0] ?? null
                      form.setData("pdf_logo", file)
                      if (file) form.setData("pdf_logo_clear", false)
                    }}
                  />
                  <p className="text-muted-foreground text-xs">
                    Shown in the header of branded Reports + Invoicing PDFs.
                    Defaults to the Rebulk wordmark when empty. PNG, JPEG, WebP,
                    or GIF.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="primary_color">Primary Color</Label>
                    <div className="flex gap-2">
                      <Input
                        id="primary_color"
                        value={form.data.primary_color}
                        onChange={(e) =>
                          form.setData("primary_color", e.target.value)
                        }
                        placeholder="#3B82F6"
                      />
                      {form.data.primary_color && (
                        <div
                          className="h-10 w-10 rounded border"
                          style={{ backgroundColor: form.data.primary_color }}
                        />
                      )}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="secondary_color">Secondary Color</Label>
                    <div className="flex gap-2">
                      <Input
                        id="secondary_color"
                        value={form.data.secondary_color}
                        onChange={(e) =>
                          form.setData("secondary_color", e.target.value)
                        }
                        placeholder="#1E40AF"
                      />
                      {form.data.secondary_color && (
                        <div
                          className="h-10 w-10 rounded border"
                          style={{ backgroundColor: form.data.secondary_color }}
                        />
                      )}
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="weight_unit">Weight Unit</Label>
                  <Select
                    value={form.data.weight_unit}
                    onValueChange={(value) =>
                      form.setData(
                        "weight_unit",
                        value as "lbs" | "tons" | "tonnes",
                      )
                    }
                  >
                    <SelectTrigger id="weight_unit" className="w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="lbs">Pounds (lbs)</SelectItem>
                      <SelectItem value="tons">US Tons</SelectItem>
                      <SelectItem value="tonnes">Metric Tonnes</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-muted-foreground text-xs">
                    Sets the weight unit for all displays and inputs across this
                    organization
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="theme">Theme</Label>
                  <Select
                    value={form.data.theme}
                    onValueChange={(value) =>
                      form.setData("theme", value as "light" | "dark")
                    }
                  >
                    <SelectTrigger id="theme" className="w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="light">Light</SelectItem>
                      <SelectItem value="dark">Dark</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-muted-foreground text-xs">
                    Sets the default theme for all users in this organization
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Inbound mail */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" /> Inbound mail
              </CardTitle>
              <CardDescription>
                Receive BOLs by email. Facilities under this org use this
                domain; each facility sets its own inbox name on the facility
                edit page.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="max-w-md space-y-3">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="email_subdomain">Subdomain</Label>
                    {!isSuperadmin && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Lock className="text-muted-foreground size-3.5" />
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-xs">
                          Only platform superadmins can change the subdomain.
                          Contact support to request a change.
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                  <div className="flex items-stretch">
                    <Input
                      id="email_subdomain"
                      value={form.data.email_subdomain}
                      disabled={!isSuperadmin}
                      onChange={(e) =>
                        form.setData(
                          "email_subdomain",
                          e.target.value
                            .toLowerCase()
                            .replace(/[^a-z0-9-]/g, ""),
                        )
                      }
                      placeholder="your-org-name"
                      maxLength={32}
                      className="rounded-r-none"
                    />
                    <span className="bg-muted text-muted-foreground inline-flex items-center rounded-r-md border border-l-0 px-3 text-sm">
                      .rebulk.com
                    </span>
                  </div>
                  <p className="text-muted-foreground text-xs">
                    Letters, numbers, and hyphens. 2–32 characters. Must be
                    unique across all Rebulk customers.
                  </p>
                  {!subdomainIsValid && (
                    <p className="text-destructive text-sm">
                      Use 2–32 letters, numbers, or hyphens.
                    </p>
                  )}
                </div>
                <div className="bg-muted/40 rounded-md border p-3 text-sm">
                  <p className="text-muted-foreground text-xs">Address shape</p>
                  <p className="mt-1 font-mono">
                    {`<inbox>@${
                      trimmedSubdomain && subdomainIsValid
                        ? trimmedSubdomain
                        : "<subdomain>"
                    }.rebulk.com`}
                  </p>
                  <p className="text-muted-foreground mt-2 text-xs">
                    Each facility picks its own {`<inbox>`} name on its edit
                    page. Operators forward BOLs to that address.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="max-w-md">
            <Button
              type="submit"
              disabled={form.processing || !subdomainIsValid}
            >
              Save Changes
            </Button>
          </div>
        </form>
      </div>
    </AppLayout>
  )
}

// Path served from /public/rebulk-logo.png (copy of lib/reports/assets/
// rebulk-logo.png — same file Prawn renders into PDFs). Single source of
// truth for the wordmark image; the icon variant is the SVG component
// `<RebulkLogo>`.
const REBULK_WORDMARK_SRC = "/rebulk-logo.png"

// Visual preview block for a logo upload slot. Renders the saved/local
// image when set, the Rebulk default (icon or wordmark) when not, and
// an X button to clear an uploaded logo back to the default.
//
//   - rebulkDefaultKind="icon"     → small Rebulk 'R' SVG (platform slot)
//   - rebulkDefaultKind="wordmark" → full Rebulk wordmark PNG (PDF slot)
//   - isLocalPreview=true          → caption hint that the image hasn't
//                                    been saved yet (local file pick)
//   - onRemove=undefined           → no X button (already on the default)
function LogoPreview({
  src,
  altWhenSet,
  rebulkDefaultKind,
  isLocalPreview,
  onRemove,
}: {
  src: string | null
  altWhenSet: string
  rebulkDefaultKind: "icon" | "wordmark"
  isLocalPreview: boolean
  onRemove?: () => void
}) {
  const isCustom = Boolean(src)
  return (
    <div className="bg-muted/20 relative inline-flex items-center gap-3 rounded-md border px-3 py-2">
      {src ? (
        <img
          src={src}
          alt={altWhenSet}
          className="h-12 w-auto rounded bg-white"
        />
      ) : rebulkDefaultKind === "icon" ? (
        <RebulkLogo className="text-foreground h-12 w-12" />
      ) : (
        <img
          src={REBULK_WORDMARK_SRC}
          alt="Rebulk logo (default)"
          className="h-12 w-auto"
        />
      )}
      <div className="flex flex-col">
        <span className="text-foreground text-xs font-medium">
          {isCustom
            ? isLocalPreview
              ? "Preview — not saved yet"
              : "Custom logo"
            : "Rebulk default"}
        </span>
        {!isCustom && (
          <span className="text-muted-foreground text-xs italic">
            Used when no logo is uploaded
          </span>
        )}
      </div>
      {onRemove && isCustom && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onRemove}
          aria-label="Remove logo and revert to Rebulk default"
          className="ml-1 h-7 w-7"
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  )
}
